import axios from 'axios';
import { clearAuthData } from './auth';

const META_OPTIMIZER_URL = import.meta.env.VITE_META_OPTIMIZER_URL || 'https://meta-optimize.conectablog.com';

export const optimizerClient = axios.create({
    baseURL: META_OPTIMIZER_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

optimizerClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

optimizerClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            clearAuthData();
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Criação de campanhas LIBERADA PARA TODOS (16/07, pedido da empresa) — allowlist
// desativada aqui e no backend (_can_create_campaigns). Pra reativar, volte a
// checar o id contra a lista espelhada de CAMPAIGN_CREATOR_IDS.
export const canCreateCampaigns = (_userId?: number | null): boolean => true;

// ========== CAMPAIGNS ==========
export interface CampaignListParams {
    project_id: number;
    account_ids?: string;
    date_from?: string;
    date_to?: string;
    status?: string;
    search?: string;
    // So no modo seletor (sem date_from/date_to): anexa `window_spend` do periodo
    // a cada linha, agregado pela maior hora coletada de cada dia — o dia corrente
    // entra parcial em vez de zerado.
    spend_from?: string;
    spend_to?: string;
    page?: number;
    per_page?: number;
}

export const getCampaigns = async (params: CampaignListParams) => {
    const { data } = await optimizerClient.get('/api/v1/campaigns/list', { params });
    return data;
};

export const getCampaignMetrics = async (campaignId: string, params: { date_from?: string; date_to?: string }) => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${campaignId}/metrics`, { params });
    return data;
};

export const updateCampaignBudget = async (campaignId: string, payload: {
    budget_type: string;
    action: string;
    value: number;
    account_id: number;
}) => {
    const { data } = await optimizerClient.post(`/api/v1/campaigns/${campaignId}/budget`, payload);
    return data;
};

export const updateCampaignStatus = async (campaignId: string, payload: {
    status: string;
    account_id: number;
}) => {
    const { data } = await optimizerClient.post(`/api/v1/campaigns/${campaignId}/status`, payload);
    return data;
};

export interface BidcapAdset {
    adset_id: string;
    name: string;
    status: string;
    optimization_goal: string;
    eligible: boolean;   // false = otimização incompatível com COST_CAP (alcance/impressões)
    current_value: number | null;   // meta de custo já ativa neste conjunto (R$), ou null
}

// Conjuntos da campanha pro modal de meta de custo (por conjunto, espelhando a Meta).
export const getBidcapAdsets = async (campaignId: string, accountId: number): Promise<{ is_cbo: boolean; data: BidcapAdset[] }> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${campaignId}/bidcap-adsets`, {
        params: { account_id: accountId },
    });
    // Backend devolve HTTP 200 + success:false nas falhas de negócio (token/permissão) — lança
    // pra não mascarar como "sem conjuntos".
    if (data && data.success === false) throw new Error(data.message || 'Falha ao carregar os conjuntos da campanha.');
    return { is_cbo: !!data?.is_cbo, data: data?.data || [] };
};

export const addCampaignBidcap = async (campaignId: string, payload: {
    account_id: number;
    items: { adset_id: string; value: number }[];
}) => {
    const { data } = await optimizerClient.post(`/api/v1/campaigns/${campaignId}/bidcap`, payload);
    return data;
};

export const getCampaignActivity = async (
    campaignId: string,
    params: { scope?: 'all' | 'campaign'; verbose?: boolean } = {},
) => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${campaignId}/activity`, {
        params: { scope: params.scope || 'all', verbose: params.verbose ? 1 : 0 },
    });
    return data;
};

export const duplicateCampaign = async (campaignId: string, payload: {
    account_id: number;
    new_name?: string;
    status?: string;
    advertiser_profile_id?: number;
}) => {
    const { data } = await optimizerClient.post(`/api/v1/campaigns/${campaignId}/duplicate`, payload);
    return data;
};

// ========== CREATE / VALIDATE / ACCOUNTS (MVP de criacao) ==========
export interface OptimizerAdAccount {
    id: number;
    account_id: string;
    name: string;
    status?: string;
    app_id?: string | null;
    // Perfil que conectou a conta (agrupa o select "Conta conectada" antes da conta de anúncio)
    meta_user_id?: string | null;
    connected_by?: string | null;
    // Id do usuario do sistema que conectou — dono do token da conexao
    connection_user_id?: number | null;
    // Espelho: a mesma conta vista por outra conexao do projeto, sem metricas
    // proprias (aponta para a linha principal). Telas que so filtram por conta
    // usam a principal (mirror_of vazio).
    mirror_of?: number | null;
}

export interface CreateCampaignPayload {
    account_id: number;
    // Perfil Anunciante escolhido no wizard — o lote inteiro é criado com o token dele
    advertiser_profile_id?: number;
    page_id?: string;
    name: string;
    objective?: string;
    optimization_goal?: string;
    daily_budget_brl?: number;
    destination_url?: string;
    display_link?: string;
    custom_tracking?: string;
    // Site: rastreamento automático por UTM. source/medium fixos no lote; campaign gerado por entidade.
    tracking_enabled?: boolean;
    utm_macro?: string;
    utm_source?: string;
    utm_medium?: string;
    message?: string;
    headline?: string;
    image_url?: string;
    image_hash?: string;
    cta?: string;
    countries?: string[];
    age_min?: number;
    age_max?: number;
    adset_name?: string;
    optimize_conversion?: boolean;
    conversion_event?: string;
    pixel_id?: string;
    gender?: string;
    device_platforms?: string[];
    user_os?: string[];
    placement_mode?: 'advantage' | 'manual';
    publisher_platforms?: string[];
    facebook_positions?: string[];
    instagram_positions?: string[];
    messenger_positions?: string[];
    budget_strategy?: string;
    cost_cap?: boolean;
    cost_cap_value?: number;
    upload_active?: boolean;
    multi_advertiser_ads?: boolean;
    interests?: { id: string; name: string }[];
    start_time?: string;
    end_time?: string;
    conversion_location?: string;
    message_template_id?: number | null;
}

// ========== MESSAGE TEMPLATES (Messenger: saudação + ice breakers) ==========
export interface IceBreaker {
    title: string;
    response?: string;
}

export interface MessageTemplate {
    id: number;
    project_id: number | null;
    name: string;
    country: string;
    type: string;
    greeting_text: string;
    ice_breakers: IceBreaker[];
    // Mensagem de acompanhamento: enviada ~1 dia depois a quem tocou no anuncio e nao
    // respondeu. GUARDADA no modelo, mas NAO enviada a' Meta na criacao do anuncio — o
    // page_welcome_message do criativo nao tem campo para isso; o envio e' um fluxo
    // separado de mensageria (RN/OTN) que ainda nao existe no backend.
    follow_up_enabled: boolean;
    follow_up_text?: string | null;
    status?: string;
    created_by?: number | null;
    created_at?: string;
    updated_at?: string;
}

export interface MessageTemplatePayload {
    project_id?: number | null;
    name: string;
    country?: string;
    type?: string;
    greeting_text: string;
    ice_breakers?: IceBreaker[];
    follow_up_enabled?: boolean;
    follow_up_text?: string | null;
}

// Modelos são pessoais (do usuário logado). Filtros opcionais por país e tipo.
export const getMessageTemplates = async (
    params?: { country?: string; type?: string }
): Promise<MessageTemplate[]> => {
    const { data } = await optimizerClient.get('/api/v1/message-templates', { params });
    return data?.data || [];
};

export const createMessageTemplate = async (payload: MessageTemplatePayload) => {
    const { data } = await optimizerClient.post('/api/v1/message-templates', payload);
    return data;
};

export const updateMessageTemplate = async (templateId: number, payload: MessageTemplatePayload) => {
    const { data } = await optimizerClient.put(`/api/v1/message-templates/${templateId}`, payload);
    return data;
};

export const deleteMessageTemplate = async (templateId: number) => {
    const { data } = await optimizerClient.delete(`/api/v1/message-templates/${templateId}`);
    return data;
};

export interface OptimizerAccountsResult {
    accounts: OptimizerAdAccount[];
    // Preenchido quando o filtro por Perfil Anunciante falhou (token expirado/sem acesso)
    profileError?: string | null;
}

// Com advertiserProfileId: só as contas que o PERFIL acessa na Meta (checagem live)
export const getOptimizerAccounts = async (
    projectId: number,
    advertiserProfileId?: number
): Promise<OptimizerAccountsResult> => {
    const { data } = await optimizerClient.get('/api/v1/campaigns/accounts', {
        params: {
            project_id: projectId,
            ...(advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : {}),
        },
    });
    return { accounts: data?.data || [], profileError: data?.profile_error || null };
};

export interface OptimizerAdvertiserProfile {
    id: number;
    name: string;
    // Responsavel: perfil sem responsavel (null) e livre pra qualquer membro
    // do projeto; com responsavel, fica exclusivo dele (outros nao podem usar).
    user_id?: number | null;
}

// Perfis Anunciantes da Conta Vinculada (novo fluxo: Projeto → Conta Vinculada → Perfil → Contas)
export const getOptimizerAdvertiserProfiles = async (metaUserId: string): Promise<OptimizerAdvertiserProfile[]> => {
    const { data } = await optimizerClient.get('/api/v1/campaigns/advertiser-profiles', {
        params: { meta_user_id: metaUserId },
    });
    return data?.data || [];
};

// Perfis da conexao dona de uma conta — usado onde a tela so tem a conta
// (duplicar campanha, criar regra), nao o meta_user_id da conexao.
export const getOptimizerAdvertiserProfilesByAccount = async (accountId: number): Promise<OptimizerAdvertiserProfile[]> => {
    const { data } = await optimizerClient.get('/api/v1/campaigns/advertiser-profiles', {
        params: { account_id: accountId },
    });
    return data?.data || [];
};

export interface OptimizerPage {
    id: string;
    name: string;
}

// Com advertiserProfileId: só as páginas que o PERFIL administra na Meta (token dele).
// Sem ele, caem as páginas da conexão — que o create_batch depois rejeita.
/**
 * Paginas da conta MAIS o aviso de escopo.
 *
 * `accountScoped: false` = o backend caiu no fallback /me/accounts (promote_pages falhou,
 * tipicamente permissao), entao a lista e' do USUARIO e nao da conta. Nesse estado a conta
 * NAO pode ser derivada da pagina escolhida — quem usa multi-conta precisa avisar na tela.
 */
export const getAccountPagesScoped = async (
    accountId: number,
    advertiserProfileId?: number,
): Promise<{ pages: OptimizerPage[]; accountScoped: boolean }> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${accountId}/pages`, {
        params: advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : {},
    });
    return {
        pages: data?.data || [],
        // Ausente = backend antigo, que so' sabia listar por usuario: trata como NAO escopado,
        // senao a tela silenciaria justo o caso que motivou a mudanca.
        accountScoped: data?.account_scoped === true,
    };
};

export const getAccountPages = async (
    accountId: number,
    advertiserProfileId?: number
): Promise<OptimizerPage[]> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${accountId}/pages`, {
        params: advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : {},
    });
    return data?.data || [];
};

export interface OptimizerPixel {
    id: string;
    name: string;
}

// Mesma regra das páginas: com advertiserProfileId, só o que o PERFIL enxerga.
export const getAccountPixels = async (
    accountId: number,
    advertiserProfileId?: number
): Promise<OptimizerPixel[]> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${accountId}/pixels`, {
        params: advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : {},
    });
    return data?.data || [];
};

export interface OptimizerInterest {
    id: string;
    name: string;
    audience?: number;
}

export const getInterestSearch = async (accountId: number, q: string): Promise<OptimizerInterest[]> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${accountId}/interests`, { params: { q } });
    return data?.data || [];
};

export const validateCampaign = async (payload: CreateCampaignPayload) => {
    const { data } = await optimizerClient.post('/api/v1/campaigns/validate', payload);
    return data;
};

export const createCampaign = async (payload: CreateCampaignPayload) => {
    const { data } = await optimizerClient.post('/api/v1/campaigns', payload);
    return data;
};

// ===== Lote (etapa 2) =====
export interface BatchAd {
    name: string;
    image_url?: string;
    image_hash?: string;
    video_id?: string;
    headline?: string;
    message?: string;
    // Messenger: personalização da saudação/perguntas DESTE anúncio (override do modelo do lote).
    // Ausente = herda o modelo escolhido no passo 1; objeto vazio = sem saudação neste anúncio.
    welcome_message?: { greeting_text: string; ice_breakers: IceBreaker[] };
    // Site: URL de destino / link de exibição DESTE anúncio (override do lote). Ausente/vazio = herda.
    destination_url?: string;
    display_link?: string;
    // Site: UTMs efetivos DESTE anúncio (o front resolve a herança lote→campanha→conjunto→anúncio).
    // Valores podem ser manuais ou macros do Facebook ({{campaign.id}} etc.).
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
}

export interface UploadImageResult {
    success: boolean;
    image_hash?: string;
    url?: string;
    message?: string;
}

// Sobe a imagem do computador pra Meta (act/adimages) e devolve o image_hash + url.
// Multipart via fetch, espelhando uploadAdVideo: o axios client força JSON, e o caminho
// JSON com base64 (que era o anterior) não sobrevive ao proxy de produção.
export const uploadAdImage = async (accountId: number, file: File, advertiserProfileId?: number | ''): Promise<UploadImageResult> => {
    const form = new FormData();
    form.append('account_id', String(accountId));
    form.append('file', file);
    if (advertiserProfileId) form.append('advertiser_profile_id', String(advertiserProfileId));
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${META_OPTIMIZER_URL}/api/v1/campaigns/upload-image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
    });
    if (!res.ok) {
        return { success: false, message: `O servidor respondeu ${res.status} ao enviar a imagem.` };
    }
    return res.json();
};

export interface AdLibraryImage {
    hash: string;
    url: string;
    thumb: string;
    name?: string;
    width?: number;
    height?: number;
}

// Biblioteca de imagens da conta (act/adimages) — pra reutilizar sem re-upload.
export const getAdImageLibrary = async (accountId: number, advertiserProfileId?: number | ''): Promise<AdLibraryImage[]> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${accountId}/adimages`, {
        params: advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : undefined,
    });
    // Backend devolve HTTP 200 com success:false nas falhas de negocio (token/permissao) — sem
    // lancar aqui, o erro viraria "[]" e a UI mostraria "biblioteca vazia" mascarando o motivo.
    if (data && data.success === false) throw new Error(data.message || 'Falha ao carregar a biblioteca de imagens.');
    return data?.data || [];
};

export interface AdLibraryVideo {
    id: string;
    title?: string;
    thumb?: string;
    length?: number;
}

// Biblioteca de vídeos da conta (act/advideos) — pra reutilizar sem re-upload.
export const getAdVideoLibrary = async (accountId: number, advertiserProfileId?: number | ''): Promise<AdLibraryVideo[]> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${accountId}/advideos`, {
        params: advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : undefined,
    });
    // Mesmo motivo do getAdImageLibrary: HTTP 200 + success:false = erro mascarado de "vazio".
    if (data && data.success === false) throw new Error(data.message || 'Falha ao carregar a biblioteca de vídeos.');
    return data?.data || [];
};

export interface PixelEvent {
    name: string;
    count: number;
}

// Eventos de conversão registrados no pixel (stats aggregation=event), por volume.
export const getPixelEvents = async (
    accountId: number,
    pixelId: string,
    advertiserProfileId?: number
): Promise<PixelEvent[]> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/${accountId}/pixels/${pixelId}/events`, {
        params: advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : {},
    });
    return data?.data || [];
};

export interface UploadVideoResult {
    success: boolean;
    video_id?: string;
    message?: string;
}

// Sobe o vídeo do computador (multipart streaming) pra Meta (act/advideos). Retorna video_id.
// Usa fetch pro browser setar o multipart/form-data + boundary (o axios client força JSON).
export const uploadAdVideo = async (accountId: number, file: File, advertiserProfileId?: number | ''): Promise<UploadVideoResult> => {
    const form = new FormData();
    form.append('account_id', String(accountId));
    form.append('file', file);
    if (advertiserProfileId) form.append('advertiser_profile_id', String(advertiserProfileId));
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${META_OPTIMIZER_URL}/api/v1/campaigns/upload-video`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
    });
    // Mesmo guard do uploadAdImage. Sem ele, uma resposta de erro do proxy (413 no teto de
    // 100 MB, 502) vem como HTML e o `res.json()` estoura SyntaxError — que o catch do
    // MediaUploadButton nao reconhece (ele testa `e instanceof TypeError`), entao o usuario
    // lia "Unexpected token '<' ... is not valid JSON".
    if (!res.ok) {
        return { success: false, message: `O servidor respondeu ${res.status} ao enviar o video.` };
    }
    return res.json();
};

export interface VideoStatusResult {
    success: boolean;
    status?: string;
    ready?: boolean;
    progress?: number;
    thumbnail_url?: string;
    message?: string;
}

// Consulta o processamento do vídeo; quando ready, traz a thumbnail auto-gerada pela Meta.
export const getVideoStatus = async (accountId: number, videoId: string, advertiserProfileId?: number | ''): Promise<VideoStatusResult> => {
    const { data } = await optimizerClient.get(`/api/v1/campaigns/video-status/${videoId}`, {
        params: { account_id: accountId, ...(advertiserProfileId ? { advertiser_profile_id: advertiserProfileId } : {}) },
    });
    return data;
};
export interface BatchAdset {
    name: string;
    interests?: { id: string; name: string }[];
    ads: BatchAd[];
    // Rastreio automático nível 'adset': valor a registrar em meta_custom_tracking.
    utm_campaign?: string;
    // Orçamento diário DESTE conjunto (só ABO); ausente = herda o valor do lote.
    daily_budget_brl?: number;
}
export interface BatchCampaign {
    name: string;
    page_id: string;
    adsets: BatchAdset[];
    // Rastreio automático (só site): nível em que o utm_campaign é registrado + valor da campanha.
    tracking_level?: 'campaign' | 'adset' | 'ad';
    utm_campaign?: string;
    // Config por campanha (Feature 2 — Estrutura por País): país, pixel, posicionamento,
    // destino e evento de conversão saem do base e passam a ser por campanha.
    countries?: string[];            // sempre 1 país
    pixel_id?: string;
    conversion_event?: string;
    destination_url?: string;
    display_link?: string;
    placement_mode?: 'advantage' | 'manual';
    publisher_platforms?: string[];
    facebook_positions?: string[];
    instagram_positions?: string[];
    messenger_positions?: string[];
    device_platforms?: string[];
    user_os?: string[];
    // Orçamento por campanha: estratégia (ABO/CBO) e valor (CBO); ausentes = herda o lote.
    budget_strategy?: string;
    daily_budget_brl?: number;
}
export interface BatchPayload {
    base: CreateCampaignPayload;
    campaigns: BatchCampaign[];
}

export const createBatch = async (payload: BatchPayload) => {
    const { data } = await optimizerClient.post('/api/v1/campaigns/batch', payload);
    return data;
};

// ========== TRAFFIC DIRECT ==========
export const getTrafficDirect = async (params: { project_id: number; start_date: string; end_date: string; account_id?: number }) => {
    const { data } = await optimizerClient.get('/api/v1/traffic-direct', { params });
    return data;
};

// ========== TRACKING ==========
export const optimizerUpdateCampaignTracking = async (campaignId: string, payload: { custom_tracking: string }) => {
    const { data } = await optimizerClient.put(`/api/v1/tracking/campaigns/${campaignId}`, payload);
    return data;
};

export const optimizerUpdateAdsetTracking = async (adsetId: string, payload: { custom_tracking: string }) => {
    const { data } = await optimizerClient.put(`/api/v1/tracking/adsets/${adsetId}`, payload);
    return data;
};

export const optimizerUpdateAdTracking = async (adId: string, payload: { custom_tracking: string }) => {
    const { data } = await optimizerClient.put(`/api/v1/tracking/ads/${adId}`, payload);
    return data;
};

export const optimizerRemoveCampaignTracking = async (campaignId: string) => {
    const { data } = await optimizerClient.delete(`/api/v1/tracking/campaigns/${campaignId}`);
    return data;
};

export const optimizerRemoveAdsetTracking = async (adsetId: string) => {
    const { data } = await optimizerClient.delete(`/api/v1/tracking/adsets/${adsetId}`);
    return data;
};

export const optimizerRemoveAdTracking = async (adId: string) => {
    const { data } = await optimizerClient.delete(`/api/v1/tracking/ads/${adId}`);
    return data;
};

// ========== ADSETS ==========
export const getAdsets = async (params: CampaignListParams & { campaign_id?: string; live_fallback?: number }) => {
    const { data } = await optimizerClient.get('/api/v1/adsets/list', { params });
    return data;
};

export const updateAdsetBudget = async (adsetId: string, payload: {
    budget_type: string;
    action: string;
    value: number;
    account_id: number;
}) => {
    const { data } = await optimizerClient.post(`/api/v1/adsets/${adsetId}/budget`, payload);
    return data;
};

export const updateAdsetStatus = async (adsetId: string, payload: {
    status: string;
    account_id: number;
}) => {
    const { data } = await optimizerClient.post(`/api/v1/adsets/${adsetId}/status`, payload);
    return data;
};

// ========== ADS ==========
export const getAds = async (params: CampaignListParams & { campaign_id?: string; adset_id?: string; live_fallback?: number }) => {
    const { data } = await optimizerClient.get('/api/v1/ads/list', { params });
    return data;
};

export const updateAdStatus = async (adId: string, payload: {
    status: string;
    account_id: number;
}) => {
    const { data } = await optimizerClient.post(`/api/v1/ads/${adId}/status`, payload);
    return data;
};

// ========== RULES ==========
export interface RuleCondition {
    metric: string;
    operator: string;
    value: number;
    period_days: number;
    aggregation: string;
}

export interface RuleActionParams {
    percentage?: number;
    max_budget?: number;
    min_budget?: number;
    amount?: number;
    budget_type?: string;
    name_suffix?: string;
    status?: string;
}

export interface CreateRulePayload {
    name: string;
    description?: string;
    project_id: number;
    account_id: number;
    entity_type: string;
    entity_ids?: string[];
    conditions: RuleCondition[];
    action_type: string;
    action_params?: RuleActionParams;
    frequency: string;
    cooldown_hours: number;
    custom_interval_hours?: number;
    // Coluna propria da regra (nao vai dentro de action_params) — obrigatorio quando
    // action_type === 'duplicate' (o backend recusa com 422 sem ele).
    advertiser_profile_id?: number;
}

export const getRules = async (params: { project_id: number }) => {
    const { data } = await optimizerClient.get('/api/v1/rules', { params });
    return data;
};

export const getRule = async (ruleId: number) => {
    const { data } = await optimizerClient.get(`/api/v1/rules/${ruleId}`);
    return data;
};

export const createRule = async (payload: CreateRulePayload) => {
    const { data } = await optimizerClient.post('/api/v1/rules', payload);
    return data;
};

export const updateRule = async (ruleId: number, payload: Partial<CreateRulePayload>) => {
    const { data } = await optimizerClient.put(`/api/v1/rules/${ruleId}`, payload);
    return data;
};

export const deleteRule = async (ruleId: number) => {
    const { data } = await optimizerClient.delete(`/api/v1/rules/${ruleId}`);
    return data;
};

export const toggleRule = async (ruleId: number) => {
    const { data } = await optimizerClient.patch(`/api/v1/rules/${ruleId}/toggle`);
    return data;
};

export const executeRule = async (ruleId: number) => {
    const { data } = await optimizerClient.post(`/api/v1/rules/${ruleId}/execute`);
    return data;
};

export const previewRule = async (payload: {
    account_id: number;
    entity_type: string;
    entity_ids?: string[];
    conditions: RuleCondition[];
    project_id: number;
}) => {
    const { data } = await optimizerClient.post('/api/v1/rules/preview', payload);
    return data;
};

// ========== LOGS ==========
export interface LogListParams {
    project_id: number;
    rule_id?: number;
    account_id?: number;
    entity_type?: string;
    action_type?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
}

export const getLogs = async (params: LogListParams) => {
    const { data } = await optimizerClient.get('/api/v1/logs', { params });
    return data;
};

export const getRuleLogs = async (ruleId: number, params: { page?: number; per_page?: number }) => {
    const { data } = await optimizerClient.get(`/api/v1/logs/rule/${ruleId}`, { params });
    return data;
};

export const getLogStats = async (params: { project_id: number; account_id?: number; date_from?: string; date_to?: string }) => {
    const { data } = await optimizerClient.get('/api/v1/logs/stats', { params });
    return data;
};

// ---- Presets de configuração de lote (pessoais, por Conta Vinculada/conexão) ----
export interface CampaignPreset {
    id: number;
    // Conexão (Conta Vinculada) dona do preset — id do Facebook, string.
    meta_user_id: string;
    name: string;
    // Blob versionado (PresetConfig) — a semântica mora em pages/MetaOptimizer/preset.ts
    config: Record<string, unknown>;
    // Ausente em preset criado antes da coluna existir → tratar como ATIVO.
    is_active?: boolean;
    updated_at: string;
}

export const listCampaignPresets = async (metaUserId: string): Promise<CampaignPreset[]> => {
    const { data } = await optimizerClient.get('/api/v1/campaigns/presets', { params: { meta_user_id: metaUserId } });
    return data?.data || [];
};

export const createCampaignPreset = async (payload: { meta_user_id: string; name: string; config: Record<string, unknown> }): Promise<CampaignPreset> => {
    const { data } = await optimizerClient.post('/api/v1/campaigns/presets', payload);
    return data?.data;
};

export const updateCampaignPreset = async (id: number, payload: { name?: string; config?: Record<string, unknown>; is_active?: boolean }): Promise<CampaignPreset> => {
    const { data } = await optimizerClient.put(`/api/v1/campaigns/presets/${id}`, payload);
    return data?.data;
};

export const deleteCampaignPreset = async (id: number): Promise<void> => {
    await optimizerClient.delete(`/api/v1/campaigns/presets/${id}`);
};
